import React, { createContext, useContext, useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import api from '../utils/api';
import tokenManager from '../utils/tokenManager';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);

  const setUserAndPersist = (userData) => {
    if (userData) {
      localStorage.setItem('user', JSON.stringify(userData));
    } else {
      localStorage.removeItem('user');
    }
    setUser(userData);
  };

  const clearAuth = () => {
    tokenManager.clearTokens();
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
  };

  const loadUserProfile = async () => {
    try {
      const response = await api.get('/users/profile');
      if (response.data?.success) {
        const profileUser = response.data.user || {};
        const fullUser = {
          ...profileUser,
          teams: Array.isArray(response.data.teams) ? response.data.teams : []
        };
        setUserAndPersist(fullUser);
        return fullUser;
      }
    } catch (error) {
      console.error('Fehler beim Laden des Nutzerprofils:', error);
    }
    return null;
  };

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        if (decoded.exp * 1000 > Date.now()) {
          setIsAuthenticated(true);
          const userData = JSON.parse(localStorage.getItem('user') || 'null');
          if (userData) setUser(userData);

          await loadUserProfile();
          return true;
        }
      } catch (error) {
        console.error('Token validation failed:', error);
      }
    }
    clearAuth();
    return false;
  };

  const login = async (token, refreshToken, initialUser) => {
    if (token) {
      tokenManager.setTokens(token, refreshToken);
      setIsAuthenticated(true);
    }

    if (initialUser) {
      setUserAndPersist(initialUser);
    }

    await loadUserProfile();
  };

  const logout = () => {
    clearAuth();
  };

  useEffect(() => {
    const initAuth = async () => {
      await checkAuth();
    };

    initAuth();
  }, []);

  const value = {
    isAuthenticated,
    user,
    login,
    logout,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};